import { supabase } from '../lib/supabase';
import {
  DiscountApplication,
  DiscountWithApplication,
  AttachmentFile,
} from '../types/discount';

// =====================================================
// 파일 업로드 (private 버킷)
// =====================================================

export async function uploadDiscountDocument(
  file: File,
  userId: string,
  discountId: string
): Promise<AttachmentFile> {
  const fileExt = file.name.split('.').pop()?.toLowerCase();
  const allowedExts = ['pdf', 'jpg', 'jpeg', 'png'];

  if (!fileExt || !allowedExts.includes(fileExt)) {
    throw new Error('PDF, JPG, PNG 파일만 업로드 가능합니다.');
  }

  if (file.size > 10 * 1024 * 1024) {
    throw new Error('파일 크기는 10MB 이하만 가능합니다.');
  }

  const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
  const filePath = `${userId}/${discountId}/${fileName}`;

  const { error } = await supabase.storage
    .from('discount-documents')
    .upload(filePath, file, {
      cacheControl: '31536000',
      upsert: false,
    });

  if (error) {
    throw new Error(`파일 업로드 실패: ${error.message}`);
  }

  return {
    name: file.name,
    url: filePath,
    size: file.size,
  };
}

// 관리자용: signed URL 생성
export async function getDocumentSignedUrl(filePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from('discount-documents')
    .createSignedUrl(filePath, 3600); // 1시간

  if (error || !data?.signedUrl) {
    throw new Error('파일 URL 생성 실패');
  }
  return data.signedUrl;
}

// =====================================================
// 수강생용 감면 서비스
// =====================================================

export interface SubmitDiscountRequest {
  application_id: string;
  discount_reason_code: number;
  discount_reason_text: string;
  discount_type: '100%' | '50%';
  student_name: string;
  birthdate?: string;
  guardian_name?: string;
  address?: string;
  guardian_phone?: string;
  contact_phone?: string;
  bank_name?: string;
  account_number?: string;
  account_holder?: string;
  privacy_consent: boolean;
  user_confirmation: boolean;
  files: File[];
}

export async function submitDiscountApplication(
  request: SubmitDiscountRequest
): Promise<{ success: boolean; data?: DiscountApplication; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: '로그인이 필요합니다.' };
    }

    if (!request.privacy_consent) {
      return { success: false, error: '개인정보 수집 이용에 동의해주세요.' };
    }
    if (!request.user_confirmation) {
      return { success: false, error: '이용자 확인사항에 동의해주세요.' };
    }

    // 1. 감면 레코드 먼저 생성 (첨부파일 경로를 위해 ID 필요)
    const { data: discount, error: insertError } = await supabase
      .from('discount_applications')
      .insert({
        application_id: request.application_id,
        user_id: user.id,
        discount_type: request.discount_type,
        discount_reason_code: request.discount_reason_code,
        discount_reason_text: request.discount_reason_text,
        student_name: request.student_name,
        birthdate: request.birthdate || null,
        guardian_name: request.guardian_name || null,
        address: request.address || null,
        guardian_phone: request.guardian_phone || null,
        contact_phone: request.contact_phone || null,
        bank_name: request.bank_name || null,
        account_number: request.account_number || null,
        account_holder: request.account_holder || null,
        privacy_consent: request.privacy_consent,
        user_confirmation: request.user_confirmation,
        attachments: [],
        status: 'submitted',
      })
      .select()
      .single();

    if (insertError) {
      if (insertError.code === '23505') {
        return { success: false, error: '이미 감면 신청이 접수되어 있습니다.' };
      }
      return { success: false, error: insertError.message };
    }

    // 2. 파일 업로드
    if (request.files.length > 0) {
      const attachments: AttachmentFile[] = [];

      for (const file of request.files) {
        const uploaded = await uploadDiscountDocument(file, user.id, discount.id);
        attachments.push(uploaded);
      }

      // 3. 첨부파일 URL 업데이트
      const { error: updateError } = await supabase
        .from('discount_applications')
        .update({ attachments })
        .eq('id', discount.id);

      if (updateError) {
        console.error('첨부파일 URL 업데이트 실패:', updateError);
      }

      discount.attachments = attachments;
    }

    return { success: true, data: discount };
  } catch (error: any) {
    return { success: false, error: error.message || '감면 신청 중 오류가 발생했습니다.' };
  }
}

// 특정 수강 신청에 대한 감면 신청 조회
export async function getDiscountByApplication(
  applicationId: string
): Promise<DiscountApplication | null> {
  const { data, error } = await supabase
    .from('discount_applications')
    .select('*')
    .eq('application_id', applicationId)
    .in('status', ['submitted', 'reviewed'])
    .maybeSingle();

  if (error) {
    console.error('감면 조회 에러:', error);
    return null;
  }
  return data;
}

// 내 감면 신청 목록 조회
export async function getMyDiscountApplications(): Promise<DiscountApplication[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('discount_applications')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('내 감면 목록 조회 에러:', error);
    return [];
  }
  return data || [];
}

// =====================================================
// 관리자용 감면 서비스
// =====================================================

export async function adminGetDiscountApplications(
  statusFilter?: string
): Promise<DiscountWithApplication[]> {
  let query = supabase
    .from('discount_applications')
    .select('*')
    .order('created_at', { ascending: false });

  if (statusFilter && statusFilter !== 'all') {
    query = query.eq('status', statusFilter);
  }

  const { data, error } = await query;

  if (error) {
    console.error('감면 목록 조회 에러:', error);
    return [];
  }

  if (!data || data.length === 0) return [];

  // application 정보 조인
  const applicationIds = Array.from(new Set(data.map(d => d.application_id)));
  const userIds = Array.from(new Set(data.map(d => d.user_id)));

  const [applicationsRes, usersRes] = await Promise.all([
    supabase
      .from('applications')
      .select('id, application_number, status, payment_status, payment_amount, class_id')
      .in('id', applicationIds),
    supabase
      .from('profiles')
      .select('id, name, phone, email, address, address_detail, birthdate')
      .in('id', userIds),
  ]);

  const appMap = new Map(
    (applicationsRes.data || []).map(a => [a.id, a])
  );
  const userMap = new Map(
    (usersRes.data || []).map(u => [u.id, u])
  );

  // class + program 정보 조인
  const classIds = Array.from(new Set(
    (applicationsRes.data || []).filter(a => a.class_id).map(a => a.class_id)
  ));

  let classMap = new Map();
  let programMap = new Map();

  if (classIds.length > 0) {
    const classRes = await supabase
      .from('program_classes')
      .select('id, class_name, program_id')
      .in('id', classIds);

    classMap = new Map(
      (classRes.data || []).map(c => [c.id, c])
    );

    const programIds = Array.from(new Set(
      (classRes.data || []).filter(c => c.program_id).map(c => c.program_id)
    ));

    if (programIds.length > 0) {
      const programRes = await supabase
        .from('courses')
        .select('id, courseName')
        .in('id', programIds);

      programMap = new Map(
        (programRes.data || []).map(p => [p.id, p])
      );
    }
  }

  return data.map(d => {
    const app = appMap.get(d.application_id);
    const userInfo = userMap.get(d.user_id);
    const classInfo = app ? classMap.get(app.class_id) : null;
    const programInfo = classInfo ? programMap.get(classInfo.program_id) : null;

    return {
      ...d,
      application: app ? {
        id: app.id,
        application_number: app.application_number,
        status: app.status,
        payment_status: app.payment_status,
        payment_amount: app.payment_amount,
        class_id: app.class_id,
      } : undefined,
      class_info: classInfo ? {
        id: classInfo.id,
        class_name: classInfo.class_name,
        program_id: classInfo.program_id,
      } : undefined,
      program_info: programInfo ? {
        id: programInfo.id,
        courseName: programInfo.courseName,
      } : undefined,
      user_info: userInfo ? {
        name: userInfo.name,
        phone: userInfo.phone,
        email: userInfo.email,
        address: userInfo.address,
        address_detail: userInfo.address_detail,
        birthdate: userInfo.birthdate,
      } : undefined,
    };
  });
}

// 관리자: 감면 확인 처리
export async function adminReviewDiscount(
  discountId: string,
  adminNote?: string
): Promise<{ success: boolean; error?: string }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { success: false, error: '로그인이 필요합니다.' };

  const { error } = await supabase
    .from('discount_applications')
    .update({
      status: 'reviewed',
      reviewed_at: new Date().toISOString(),
      reviewed_by: user.id,
      admin_note: adminNote || null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', discountId);

  if (error) {
    return { success: false, error: error.message };
  }
  return { success: true };
}

// 상태 라벨/색상
export function getDiscountStatusLabel(status: string): string {
  switch (status) {
    case 'submitted': return '접수됨';
    case 'reviewed': return '확인완료';
    default: return status;
  }
}

export function getDiscountStatusColor(status: string): string {
  switch (status) {
    case 'submitted': return 'bg-amber-50 text-amber-600 border-amber-200';
    case 'reviewed': return 'bg-green-50 text-green-600 border-green-200';
    default: return 'bg-gray-50 text-gray-600 border-gray-200';
  }
}
